import React, { useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { useTranslation } from 'react-i18next'
import { Popup, Table } from 'semantic-ui-react'
import { formatNumber } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'
import { OptionContent } from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ForageProdStep/style'
import { CO2OptionsType } from '~/api/services/co2'
import { fertilizerDataFields } from '../OrganicFertilizers'
import CustomIcon from '~/components/CustomIcon'
import { MineralFertilizerType } from '~/api/services/climaCheck'
import EditFertilizers from '../EditFertilizers'

export const mineralDataFields = [
  {
    name: 'Stickstoff (kg/dt)',
    key: 'n',
    type: 'input'
  },
  {
    name: 'Phosphat (P2O5) (kg/dt)',
    key: 'p2o5',
    type: 'input'
  },
  {
    name: '<span>Kalium (K<sub>2</sub>O) (kg/dt)</span>',
    key: 'k2o',
    type: 'input'
  }
]
interface Props {
  mineralKey: 'grassSilage.mineralFertilizers' | 'maizeSilage.mineralFertilizers'
  options: CO2OptionsType
}

const MineralFertilizers: React.FC<Props> = ({ mineralKey, options }) => {
  const { t } = useTranslation()
  const { mineralFertilizers, mineralFertilizerApplicationTechniques } = options
  const { control, getValues, setValue, clearErrors } = useFormContext()

  const [fertilizerData, setFertilizerData] = useState<MineralFertilizerType>(null)

  const { fields, replace, update } = useFieldArray({
    control,
    name: mineralKey,
    keyName: 'key'
  })

  const tableColumnHeaders = [
    {
      name: 'Düngeart',
      key: 'label',
      type: 'fertilizer-fields'
    },
    {
      name: 'Ausbringmenge',
      key: 'value',
      type: 'input'
    },
    {
      name: 'Ausbringtechnik',
      key: 'co2MineralFertilizerApplicationTechniqueId',
      type: 'select',
      optionsKey: 'mineralFertilizerApplicationTechniques'
    }
  ];

  function renderInputCellValue(name) {
    return <FormFields.Input
      placeholder={'dt/ha'}
      name={name}
      suffix=' dt/ha'
      id={name}
      onChange={(event) => {
        setValue(name, event?.target?.value?.replace('dt/ha', ''))
        clearErrors(name)
      }}
      rules={{
        required: true,
        validate: (value) => {
          const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
          if (transformValue > 20) {
            return 'Die Ausbringmenge darf 20 dt/ha nicht überschreiten.'
          }
          return true
        }
      }}
      control={control}
      typeInput={'controlled-inline-error'}
      withoutAsterisk
      noErrorMessage
      allowDecimals
      decimalScale={2}
    />
  }

  function renderSelect(name) {
    return <FormFields.Select
      name={name}
      style={{ width: 380 }}
      onChange={(e, data) => {
        setValue(name, data.value)
        clearErrors(name)
      }}
      upward
      className={'menu-contain'}
      rules={{
        required: true
      }}
      placeholder={'Art der Ausbringungstechhnik'}
      control={control}
      scrolling
      withoutAsterisk
      noErrorMessage
      options={mineralFertilizerApplicationTechniques.map(mineralFert => ({ value: mineralFert.id, text: mineralFert.label }))}
    />
  }

  function renderFertilizerFieldCell(mineralFertilizer) {
    return <>
      <div>{mineralFertilizer.label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <small style={{ fontSize: '85%' }}>
          <>{`N: ${mineralFertilizer.n} kg/${mineralFertilizer?.unit || ''},  `}</>
          <>P<sub>2</sub>O<sub>5</sub>{`: ${mineralFertilizer.p2o5} kg/${mineralFertilizer?.unit || ''},  `}</>
          <>K<sub>2</sub>O{`: ${mineralFertilizer.k2o} kg/${mineralFertilizer?.unit || ''}`}</>
        </small>
        <div style={{ display: 'flex', gap: 5, marginLeft: 7 }}>
          <InfoPopup
            position={'top center'}
            style={{ minWidth: 280 }}
            styleIcon={{ fontSize: '1.2em' }}
            content={<small style={{ fontSize: '85%' }}>
              {fertilizerDataFields.map(fertilizeField => {
                return <div
                  key={fertilizeField.key}
                  style={{ wordBreak: 'keep-all' }}
                  dangerouslySetInnerHTML={{
                    __html: `${fertilizeField.name}: 
                  ${formatNumber(mineralFertilizer[fertilizeField.key]?.replace(',', '.'), 2)} 
                  kg/${mineralFertilizer.unit} <span style="font-style: italic; font-size: 12px !important;">(Nutzbar: ${mineralFertilizer[`${fertilizeField.key}Usable`]}%)</span>`
                  }}
                />
              })}
            </small>}
          />
          <Popup
            content={<div>
              <div>{t('Change')}</div>
            </div>}
            trigger={<CustomIcon
              name={'pencil'}
              onClick={() => setFertilizerData(mineralFertilizer)}
              style={{ fontSize: '1.2em' }}
            />}
          />
        </div>
      </div>
    </>
  }

  function renderCell(mineralFertilizer, name, column) {
    switch (column.type) {
      case 'input':
        return renderInputCellValue(name)
      case 'select':
        return renderSelect(name)
      case 'fertilizer-fields':
        return renderFertilizerFieldCell(mineralFertilizer)
      default:
        return mineralFertilizer[column.key]
    }
  }

  function renderTableBody() {
    return fields.map((mineralFertilizer, indexFertilizer) => {
      return <Table.Row key={mineralFertilizer.key}>
        {tableColumnHeaders.map(column => {
          const name = `${mineralKey}[${indexFertilizer}].${column.key}`
          return <Table.Cell singleLine key={`${mineralFertilizer.key}-${column.key}`}>
            {renderCell(mineralFertilizer, name, column)}
          </Table.Cell>
        })}
      </Table.Row>
    })
  }

  function renderTable() {
    return <Table columns={3}>
      <Table.Header>
        <Table.Row>
          {tableColumnHeaders.map(header => {
            return <Table.HeaderCell singleLine key={header.key}>
              {header.name}
            </Table.HeaderCell>
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table>
  }

  function renderEditFertilizerData() {
    return <EditFertilizers
      fertilizerData={fertilizerData}
      handleClose={() => setFertilizerData(null)}
      handleSubmit={(fertilizerData, updateData) => {
        const index = fields.findIndex(fertilizer => fertilizer['id'] === fertilizerData.id)

        if (index >= 0) {
          update(index, {
            ...fertilizerData,
            ...updateData
          })
          setFertilizerData(null)
        }
      }}
    />
  }

  return <>
    {renderEditFertilizerData()}
    <OptionContent>
      <FormFields.Select
        label={'Welche Mineraldünger haben Sie aufgebracht?'}
        control={control}
        placeholder='Art des Mineraldünger'
        id={mineralKey}
        name=''
        value={fields.map(fertilizer => fertilizer['co2MineralFertilizerId'] ?? fertilizer['id'])}
        options={mineralFertilizers.map(type => ({ value: type.id, text: type.label }))}
        onChange={(_, { value }: { value: number[] }) => {
          const minerals = getValues(mineralKey) || []
          const includedmineralFertilizerValues = value.map(id => {
            const existing = minerals.find(field => (field['co2MineralFertilizerId'] ?? field['id']) === id)
            if (existing) {
              return existing
            }
            const base = mineralFertilizers.find(fertilizer => fertilizer.id === id)
            return { ...base, value: '', co2MineralFertilizerApplicationTechniqueId: '' }
          })

          if (includedmineralFertilizerValues.length > 0) {
            const targetElement = document.getElementById(mineralKey)
            targetElement?.classList?.remove('error');
          }
          replace(includedmineralFertilizerValues)
        }}
        multiple
        search
        noResultsMessage={t('No results found....')}
        noErrorMessage
        withoutAsterisk
      />
      {fields.length > 0 && renderTable()}
    </OptionContent>
  </>
}
export default MineralFertilizers